import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { InvoiceDto, InvoiceSendRecipientsDto, SendInvoicesResultDto } from '../../../../common/interfaces/invoicing.interface';
import { getSendRecipients, sendInvoice, sendInvoices } from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';

/** The server takes at most this many invoices per request, for both routes. */
const CHUNK = 200;

const chunks = <T,>(items: T[]): T[][] =>
  Array.from({ length: Math.ceil(items.length / CHUNK) }, (_, index) => items.slice(index * CHUNK, (index + 1) * CHUNK));

interface SendInvoicesDialogProps {
  kindergartenId: string;
  /** Issued invoices to email; one is shown recipient by recipient, several as a summary. */
  invoices: Pick<InvoiceDto, 'id' | 'invoiceNumber' | 'childFullName'>[];
  onClose: () => void;
  /** Called once the request went through, before the dialog closes. */
  onSent: (result: SendInvoicesResultDto) => void;
}

/**
 * The one way an issued invoice is emailed: one from its row or its page, a selection, or
 * the month's. It asks the server first who each invoice would go to, so the admin sees
 * the payer and the other parents before anything goes out.
 *
 * "Also send to the other parents" starts from the contracts. All of them say yes: ticked;
 * none: unticked; some: indeterminate, meaning "as each contract says". Until the admin
 * clicks it, the send carries no `toAllParents` at all and the server follows each
 * contract; a ticked or unticked box that only mirrors the contracts would mean the same,
 * but leaving it out keeps a contract changed meanwhile in charge. Once clicked, the box is
 * an explicit choice for this send and overrides every contract, either way.
 */
const SendInvoicesDialog: React.FC<SendInvoicesDialogProps> = ({ kindergartenId, invoices, onClose, onSent }) => {
  const { t } = useTranslation();
  const [recipients, setRecipients] = useState<InvoiceSendRecipientsDto[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // null until the admin clicks the box: each invoice then follows its child's contract
  const [choice, setChoice] = useState<boolean | null>(null);
  const [sending, setSending] = useState(false);
  // emailing parents cannot be taken back, so a double click on Send must not mail twice
  const inFlight = useRef(false);
  const single = invoices.length === 1;

  useEffect(() => {
    let current = true;
    Promise.all(chunks(invoices.map(invoice => invoice.id)).map(ids => getSendRecipients(kindergartenId, ids)))
      .then(results => current && setRecipients(results.flatMap(result => result.results ?? [])))
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
    };
    // the invoices are fixed for the dialog's life; a new array each render must not refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId]);

  const loading = !recipients && !loadFailed;
  // without an answer about who gets what, every invoice is still sent as its contract says
  const sendable = recipients ? recipients.filter(entry => !!entry.payer) : null;
  const unsendable = recipients ? recipients.filter(entry => !entry.payer) : [];
  const targetIds = sendable ? sendable.map(entry => entry.invoiceId) : invoices.map(invoice => invoice.id);
  // the box only matters for invoices that have somebody to copy
  const withOthers = (sendable ?? []).filter(entry => entry.otherParents.length > 0);
  const contractYes = withOthers.filter(entry => entry.invoiceToAllParents).length;
  const mixed = choice === null && contractYes > 0 && contractYes < withOthers.length;
  const checked = choice ?? (withOthers.length > 0 && contractYes === withOthers.length);
  const copiesFor = (entry: InvoiceSendRecipientsDto) => choice ?? entry.invoiceToAllParents;
  const emails = (sendable ?? []).reduce((sum, entry) => sum + 1 + (copiesFor(entry) ? entry.otherParents.length : 0), 0);

  const send = async () => {
    if (inFlight.current || !targetIds.length) return;
    inFlight.current = true;
    setSending(true);
    const toAllParents = choice ?? undefined;
    // the parts of a bulk send that went through before one failed: those invoices are
    // queued, so the list must show it and a retry must not send them again
    const done: SendInvoicesResultDto[] = [];
    const summed = (): SendInvoicesResultDto => ({
      queuedCount: done.reduce((sum, part) => sum + part.queuedCount, 0),
      skippedCount: done.reduce((sum, part) => sum + part.skippedCount, 0),
      outcomes: done.flatMap(part => part.outcomes ?? [])
    });
    try {
      if (single) {
        const invoice = invoices[0];
        const result = await sendInvoice(kindergartenId, invoice.id, toAllParents);
        const outcome = result.results?.outcomes?.[0];
        if (outcome && !outcome.queued) {
          createNotification(MESSAGE_TYPE.ERROR, outcome.reason ?? t('invoices.send.failed'));
        } else {
          createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.send.queuedOne', { number: invoice.invoiceNumber }));
        }
        if (result.results) onSent(result.results);
      } else {
        for (const ids of chunks(targetIds)) {
          const part = (await sendInvoices(kindergartenId, ids, toAllParents)).results;
          if (part) done.push(part);
        }
        const result = summed();
        const { queuedCount, skippedCount } = result;
        createNotification(
          skippedCount ? MESSAGE_TYPE.ERROR : MESSAGE_TYPE.SUCCESS,
          skippedCount
            ? t('invoices.bulk.sendPartial', { queued: queuedCount, skipped: skippedCount })
            : t('invoices.bulk.queued', { count: queuedCount })
        );
        onSent(result);
      }
      onClose();
    } catch (error: any) {
      const reason = error?.response?.data?.message ?? (single ? t('invoices.send.failed') : t('invoices.send.bulkFailed'));
      if (!single && done.length) {
        // part of it went: say how much, refresh the list behind, and close - reopening
        // from the list offers only what is still unsent
        const result = summed();
        createNotification(
          MESSAGE_TYPE.ERROR,
          t('invoices.send.bulkInterrupted', { count: targetIds.length, queued: result.queuedCount, reason })
        );
        onSent(result);
        onClose();
      } else {
        createNotification(MESSAGE_TYPE.ERROR, reason);
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  const one = single && recipients ? (recipients[0] ?? null) : null;

  const recipientRow = (fullName: string, email: string, payer: boolean) => (
    <Box component="li" key={email} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{fullName}</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{email}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: payer ? 'primary.main' : 'text.secondary', flexShrink: 0 }}>
        {payer ? t('invoices.recipients.payer') : t('invoices.recipients.copy')}
      </Typography>
    </Box>
  );

  return (
    <Dialog open onClose={sending ? undefined : onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 520, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 600 }}>
        {single
          ? t('invoices.sendDialog.titleOne', { number: invoices[0].invoiceNumber ?? invoices[0].childFullName })
          : t('invoices.steps.send.confirmTitle', { count: invoices.length })}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} aria-label={t('invoices.sendDialog.loading')} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 14 }}>
              {single ? t('invoices.sendDialog.confirmOne') : t('invoices.steps.send.confirm').trim()}
            </Typography>

            {loadFailed && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {t('invoices.sendDialog.loadFailed')}
              </Alert>
            )}

            {one && !one.payer && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {t('invoices.sendDialog.payerNoEmail')}
              </Alert>
            )}

            {sendable && sendable.length > 0 && (
              <Box>
                {withOthers.length > 0 ? (
                  <>
                    <FormControlLabel
                      control={<Checkbox checked={checked} indeterminate={mixed} onChange={event => setChoice(event.target.checked)} />}
                      label={<Typography sx={{ fontSize: 14 }}>{t('invoices.sendDialog.allParents')}</Typography>}
                    />
                    {mixed && (
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', pl: 4 }}>
                        {t('invoices.sendDialog.perContract', { count: contractYes })}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('invoices.sendDialog.noOthers')}</Typography>
                )}
              </Box>
            )}

            {one?.payer && (
              <Box component="section" aria-label={t('invoices.sendDialog.recipients')}>
                <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                  {t('invoices.sendDialog.recipients')}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {recipientRow(one.payer.fullName, one.payer.email, true)}
                  {copiesFor(one) && one.otherParents.map(parent => recipientRow(parent.fullName, parent.email, false))}
                </Box>
              </Box>
            )}

            {!single && sendable && (
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {t('invoices.sendDialog.invoices', { count: sendable.length })} · {t('invoices.sendDialog.emails', { count: emails })}
              </Typography>
            )}

            {!single && unsendable.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 13, color: 'warning.main' }}>
                  {t('invoices.sendDialog.notSendable', { count: unsendable.length })}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13 }}>
                  {unsendable.map(entry => (
                    <li key={entry.invoiceId}>
                      {entry.invoiceNumber ? `${entry.invoiceNumber} · ${entry.childFullName}` : entry.childFullName}
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Neutral}
          variant="outlined"
          label={t('common.cancel')}
          onClick={onClose}
          disabled={sending}
        />
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          label={t('invoices.steps.send.button', { count: targetIds.length })}
          onAsyncAction={send}
          disabled={loading || sending || !targetIds.length}
        />
      </DialogActions>
    </Dialog>
  );
};

export default SendInvoicesDialog;
